import { ChevronDown } from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import {
  NavLink,
  useLocation,
  useMatch,
  useResolvedPath,
} from "react-router-dom";

export type SurfaceTabIcon = ComponentType<{
  size?: number | string;
  className?: string;
}>;

export type SurfaceTab = {
  id: string;
  label: string;
  to: string;
  icon?: SurfaceTabIcon;
  badge?: string | number | null;
  end?: boolean;
  disabled?: boolean;
  reloadDocument?: boolean;
  match?: (pathname: string) => boolean;
  testId?: string;
  dataAttributes?: Record<
    `data-${string}`,
    string | number | boolean | null | undefined
  >;
};

export type SurfaceTabsProps = {
  items: readonly SurfaceTab[];
  activeId?: string | null;
  ariaLabel: string;
  className?: string;
  testId?: string;
};

export type ResponsiveSurfaceTabsTestIds = {
  root?: string;
  itemPrefix?: string;
  itemIds?: Record<string, string>;
  mobileTrigger?: string;
  mobileMenu?: string;
  mobileItemPrefix?: string;
  mobileItemIds?: Record<string, string>;
};

export type ResponsiveSurfaceTabsProps = SurfaceTabsProps & {
  mobileMenuTitle?: string;
  desktopClassName?: string;
  mobileClassName?: string;
  triggerClassName?: string;
  testIds?: ResponsiveSurfaceTabsTestIds;
};

const joinClassNames = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

const resolveDataAttributes = (attributes?: SurfaceTab["dataAttributes"]) => {
  if (!attributes) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(attributes).filter(
      ([key, value]) => key.startsWith("data-") && value != null,
    ),
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const isSurfaceTabActive = (
  item: SurfaceTab,
  pathname: string,
): boolean => {
  if (item.match) {
    return item.match(pathname);
  }
  if (pathname === item.to) {
    return true;
  }
  if (item.end || item.to === ".") {
    return false;
  }
  return pathname.startsWith(item.to.endsWith("/") ? item.to : `${item.to}/`);
};

const SurfaceTabLink = ({
  item,
  activeId,
}: {
  item: SurfaceTab;
  activeId?: string | null;
}) => {
  const location = useLocation();
  const resolvedPath = useResolvedPath(item.to);
  const routeMatch = useMatch({
    path: resolvedPath.pathname,
    end: item.end ?? item.to === ".",
  });
  const active = activeId
    ? activeId === item.id
    : item.match
      ? item.match(location.pathname)
      : Boolean(routeMatch);
  const Icon = item.icon;
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (item.disabled) {
      event.preventDefault();
    }
  };

  return (
    <NavLink
      to={item.to}
      end={item.end}
      {...resolveDataAttributes(item.dataAttributes)}
      data-testid={item.testId}
      data-state={active ? "active" : "inactive"}
      aria-current={active ? "page" : undefined}
      aria-disabled={item.disabled ? "true" : undefined}
      reloadDocument={item.reloadDocument}
      onClick={handleClick}
      className={joinClassNames(
        "yst-motion-color relative -mb-px inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap border-b-2 px-1 pb-3 pt-1 text-sm",
        active
          ? "border-[var(--primary)] font-bold text-[var(--primary)]"
          : "border-transparent font-semibold text-[var(--iron-grey)] hover:text-[var(--primary)]",
        item.disabled ? "pointer-events-none opacity-50" : "",
      )}
    >
      {Icon ? <Icon size={16} /> : null}
      <span>{item.label}</span>
      {item.badge != null ? (
        <span className="rounded-full bg-[var(--strawberry)] px-2 py-0.5 text-[10px] font-bold text-white">
          {item.badge}
        </span>
      ) : null}
    </NavLink>
  );
};

export const SurfaceTabs = ({
  items,
  activeId,
  ariaLabel,
  className,
  testId,
}: SurfaceTabsProps) => (
  <nav
    aria-label={ariaLabel}
    data-testid={testId}
    className={joinClassNames(
      "flex w-full flex-wrap items-end gap-2 border-b border-[var(--border)]",
      className,
    )}
  >
    {items.map((item) => (
      <SurfaceTabLink key={item.id} item={item} activeId={activeId} />
    ))}
  </nav>
);

const normalizeResponsiveTestIds = (
  testIds?: ResponsiveSurfaceTabsTestIds,
  fallbackRoot?: string,
): Required<ResponsiveSurfaceTabsTestIds> => ({
  root: testIds?.root ?? fallbackRoot ?? "surface-section-tabs",
  itemPrefix: testIds?.itemPrefix ?? "surface-section-tab",
  itemIds: testIds?.itemIds ?? {},
  mobileTrigger:
    testIds?.mobileTrigger ?? "surface-section-tabs-mobile-trigger",
  mobileMenu: testIds?.mobileMenu ?? "surface-section-tabs-mobile-menu",
  mobileItemPrefix:
    testIds?.mobileItemPrefix ?? "surface-section-tabs-mobile-item",
  mobileItemIds: testIds?.mobileItemIds ?? {},
});

export const ResponsiveSurfaceTabs = ({
  items,
  activeId,
  ariaLabel,
  className,
  desktopClassName,
  mobileClassName,
  triggerClassName,
  testId,
  testIds,
  mobileMenuTitle = "Navigate sections",
}: ResponsiveSurfaceTabsProps) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const resolvedTestIds = normalizeResponsiveTestIds(testIds, testId);
  const resolvedActiveItem = useMemo(() => {
    if (activeId) {
      return items.find((item) => item.id === activeId) ?? items[0] ?? null;
    }
    return (
      items.find((item) => isSurfaceTabActive(item, location.pathname)) ??
      items[0] ??
      null
    );
  }, [activeId, items, location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const closeOnEscape = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      setMobileOpen(false);
    }
  };

  return (
    <>
      <SurfaceTabs
        items={items.map((item) => ({
          ...item,
          testId:
            item.testId ??
            resolvedTestIds.itemIds[item.id] ??
            `${resolvedTestIds.itemPrefix}-${item.id}`,
        }))}
        activeId={activeId}
        ariaLabel={ariaLabel}
        testId={resolvedTestIds.root}
        className={joinClassNames(
          "hidden lg:flex",
          className,
          desktopClassName,
        )}
      />

      <div
        className={joinClassNames("relative lg:hidden", mobileClassName)}
        onKeyDown={closeOnEscape}
      >
        <button
          type="button"
          data-testid={resolvedTestIds.mobileTrigger}
          aria-haspopup="menu"
          aria-expanded={mobileOpen}
          aria-controls={resolvedTestIds.mobileMenu}
          aria-label={mobileMenuTitle}
          onClick={() => setMobileOpen((open) => !open)}
          className={joinClassNames(
            "yst-motion-surface flex w-full items-center justify-between gap-3 rounded-[10px] border border-[var(--border)] bg-[var(--surface-light,#ffffff)] px-4 py-2.5 text-left shadow-sm transition-colors hover:border-[var(--primary)]/30 hover:bg-[var(--muted,#f4f6f3)]",
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-[var(--iron-grey)]">
            {resolvedActiveItem?.icon ? (
              <resolvedActiveItem.icon size={16} />
            ) : null}
            <span className="truncate">
              {resolvedActiveItem?.label ?? "Select section"}
            </span>
          </span>
          <span className="flex items-center gap-2">
            {resolvedActiveItem?.badge != null ? (
              <span className="rounded-full bg-[var(--strawberry)] px-2 py-0.5 text-[10px] font-bold text-white">
                {resolvedActiveItem.badge}
              </span>
            ) : null}
            <ChevronDown
              size={16}
              className={joinClassNames(
                "text-[var(--iron-grey)] transition-transform",
                mobileOpen ? "rotate-180" : "",
              )}
              aria-hidden="true"
            />
          </span>
        </button>

        {mobileOpen ? (
          <nav
            id={resolvedTestIds.mobileMenu}
            aria-label={ariaLabel}
            data-testid={resolvedTestIds.mobileMenu}
            className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 overflow-hidden rounded-[12px] border border-[var(--border)] bg-white py-2 shadow-[0_18px_45px_rgba(15,23,42,0.16)]"
          >
            {items.map((item) => {
              const active =
                activeId === item.id ||
                (!activeId && isSurfaceTabActive(item, location.pathname));
              const Icon = item.icon;
              const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
                if (item.disabled) {
                  event.preventDefault();
                  return;
                }
                setMobileOpen(false);
              };
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.end}
                  {...resolveDataAttributes(item.dataAttributes)}
                  data-testid={
                    resolvedTestIds.mobileItemIds[item.id] ??
                    `${resolvedTestIds.mobileItemPrefix}-${item.id}`
                  }
                  aria-current={active ? "page" : undefined}
                  aria-disabled={item.disabled ? "true" : undefined}
                  reloadDocument={item.reloadDocument}
                  onClick={handleClick}
                  className={joinClassNames(
                    "yst-motion-color flex items-center gap-3 px-5 py-3 text-sm",
                    active
                      ? "border-l-4 border-[var(--primary)] bg-[var(--primary)]/10 font-semibold text-[var(--primary)]"
                      : "font-medium text-[var(--iron-grey)] hover:bg-[var(--muted,#f4f6f3)]",
                    item.disabled ? "pointer-events-none opacity-50" : "",
                  )}
                >
                  {Icon ? <Icon size={18} /> : null}
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.badge != null ? (
                    <span className="rounded-full bg-[var(--strawberry)] px-2 py-0.5 text-[10px] font-bold text-white">
                      {item.badge}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </nav>
        ) : null}
      </div>
    </>
  );
};
